/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import { WebMercatorViewport } from '@deck.gl/core';
import { atom, useAtomValue } from 'jotai';
import { atomFamily } from 'jotai-family';
import React from 'react';

import { settledViewStateAtom, viewStateAtom } from './atoms';
import type { ViewStateProps } from './deckgl-types';
import type { GeometryVariant } from '../services/featuresApi';

/** [minLng, minLat, maxLng, maxLat], the order the features API expects. */
export type Bbox = [number, number, number, number];

type Camera = ViewStateProps & { width?: number; height?: number };

/** The coordinate range the features API stores and filters geometry in. */
const MIN_LONGITUDE = -180;
const MAX_LONGITUDE = 180;
const MIN_LATITUDE = -90;
const MAX_LATITUDE = 90;
const LONGITUDE_SPAN = MAX_LONGITUDE - MIN_LONGITUDE;

/** Pixels deck.gl projects the whole world into at zoom 0 (@deck.gl/core 9.3.4). */
const WORLD_SIZE_PIXELS = 512;

/**
 * A line drawn half a pixel out already covers different pixels than the real one. A
 * CSS pixel, which equals a rendered pixel only because Map.tsx passes
 * useDevicePixels={false}.
 */
const VISIBLE_DEVIATION_PIXELS = 0.5;

/**
 * Fraction of the viewport span added to each side of a viewport geometry
 * request. A later pan that keeps the viewport inside the padded box reuses the
 * geometry already fetched, so small moves cost no request.
 */
export const VIEWPORT_BBOX_PADDING = 0.25;

/**
 * Widest map canvas the API's bound on a viewport request is built from, in CSS
 * pixels. Restated from apps/server/src/geometry/geometry-scope.ts, as the four
 * constants above are, because the server does not consume this bundle.
 */
const MAX_CANVAS_PIXELS = 5120;

/**
 * On-screen deviation, in pixels, of geometry generalized to `toleranceDegrees`
 * drawn at one zoom and latitude.
 *
 * ST_SimplifyPreserveTopology (Douglas-Peucker) moves no vertex further than its
 * tolerance, so this bounds the error of any dataset rather than describing the
 * data currently loaded. Dividing by cos(latitude) is the Mercator stretch: one
 * tolerance is more visible the further north it is drawn, which is why the
 * switch point is computed per camera instead of fixed per column.
 */
export function deviationPixels(toleranceDegrees: number, zoom: number, latitude: number): number {
  return (
    (toleranceDegrees * WORLD_SIZE_PIXELS * 2 ** zoom) /
    (LONGITUDE_SPAN * Math.cos((latitude * Math.PI) / 180))
  );
}

/**
 * Widest box, in degrees, the API serves a resolution over that it will not serve
 * whole: the padded MAX_CANVAS_PIXELS canvas at the zoom the rung above becomes
 * visible at, which is the lowest zoom this resolution is ever asked for.
 *
 * The same formula the API applies before it runs the query
 * (apps/server/src/geometry/geometry-scope.ts). The two have to move together, so
 * a test on each side asserts the same explicit numbers.
 */
export function maxViewportSpanDegrees(
  escalatedFromToleranceDegrees: number,
  latitude: number
): number {
  const visibleFromZoom = Math.log2(
    (VISIBLE_DEVIATION_PIXELS * LONGITUDE_SPAN * Math.cos((latitude * Math.PI) / 180)) /
      (escalatedFromToleranceDegrees * WORLD_SIZE_PIXELS)
  );
  const canvasSpanDegrees =
    (MAX_CANVAS_PIXELS * LONGITUDE_SPAN) / (WORLD_SIZE_PIXELS * 2 ** visibleFromZoom);
  return canvasSpanDegrees * (1 + 2 * VIEWPORT_BBOX_PADDING);
}

/**
 * The latitude on screen that shows a generalization most: the edge furthest from
 * the equator, not the centre. One view spans latitudes -- the initial national
 * camera covers 11.7 N to 59.4 N, where a tolerance is 1.5x as visible as at its
 * 39.4 N centre -- so a centre-latitude test draws visibly wrong geometry along
 * the top of the map. Before the canvas is measured the centre is the only
 * latitude there is.
 */
export function worstVisibleLatitude(camera: Camera): number {
  const bbox = cameraBbox(camera);
  if (bbox == null) {
    return Math.abs(camera.latitude);
  }
  return Math.max(Math.abs(bbox[1]), Math.abs(bbox[3]));
}

/**
 * A precomputed generalized geometry column: the variant name the API serves it
 * under, and the tolerance in degrees the migration generated it with.
 */
export type GeometryRung<V extends GeometryVariant = GeometryVariant> = {
  variant: V;
  toleranceDegrees: number;
};

/**
 * The resolutions one table offers, coarsest first, and distinct tolerances.
 *
 * `whole` is fetched entire; `viewport` holds resolutions the API refuses to serve
 * whole (their whole-table response does not fit its budget), so they are fetched
 * for the camera viewport with the whole-table set left underneath as the backdrop.
 * The API also bounds how wide that box may be, from the tolerance of the rung
 * above -- the same half-pixel rule as here. The exact geometry ends every ladder
 * and is not listed.
 */
export type GeometryLadder<W extends GeometryVariant = GeometryVariant> = {
  whole: readonly [GeometryRung<W>, ...GeometryRung<W>[]];
  viewport: readonly GeometryRung[];
};

/** Deviation-free geometry, so this rung ends every ladder. */
const EXACT_GEOMETRY: GeometryRung = { variant: 'full', toleranceDegrees: 0 };

// Every tolerance below is the one its migration generated the column with: the
// zoom a column may be drawn at is derived from the column itself, so the two
// cannot drift apart.

/**
 * County boundaries (reference.usa_census_counties).
 *
 * The two viewport rungs exist because 0.002 deg becomes visible at zoom 7.2,
 * where a padded viewport can hold 57 MB of full-resolution county text --
 * past the API's 32 MB response budget. They carry the band to zoom 11.2, where
 * the worst measured viewport of full-resolution counties is 10 MB.
 */
export const COUNTY_GEOMETRY_LADDER: GeometryLadder<'coarse' | 'simplified'> = {
  whole: [
    { variant: 'coarse', toleranceDegrees: 0.01 }, // migration 000027
    { variant: 'simplified', toleranceDegrees: 0.002 }, // migration 000023
  ],
  viewport: [
    { variant: 'fine', toleranceDegrees: 0.0005 }, // migration 000030
    { variant: 'finer', toleranceDegrees: 0.000125 }, // migration 000030
  ],
};

/** NIFC perimeters (features.nifc_bounds and its history table). */
export const NIFC_BOUNDS_GEOMETRY_LADDER: GeometryLadder<'simplified'> = {
  whole: [{ variant: 'simplified', toleranceDegrees: 0.0005 }], // migration 000026
  viewport: [],
};

/** Tribal boundaries (reference.tribal_boundaries). */
export const TRIBAL_GEOMETRY_LADDER: GeometryLadder<'simplified'> = {
  whole: [{ variant: 'simplified', toleranceDegrees: 0.001 }], // migration 000028
  viewport: [],
};

/**
 * Tolerance of the coarsest rung of the ladder whose deviation is invisible at
 * this camera, which identifies that rung -- a ladder's tolerances are distinct.
 * Zero is the exact geometry.
 */
function faithfulTolerance(ladder: GeometryLadder, camera: Camera): number {
  // The one place the half-pixel threshold is applied, and the latitude is resolved
  // once for it rather than per rung: resolving builds a viewport, and this runs for
  // every rung on every frame the live view state emits during a drag. At exactly
  // the threshold the generalization counts as visible, so the boundary case draws
  // the finer resolution.
  //
  // `camera.zoom` is the scale everywhere on the canvas, which holds because the
  // camera is level.
  const latitude = worstVisibleLatitude(camera);
  const faithful = (rungs: readonly GeometryRung[]): GeometryRung | undefined =>
    rungs.find(
      rung =>
        deviationPixels(rung.toleranceDegrees, camera.zoom, latitude) < VISIBLE_DEVIATION_PIXELS
    );
  return (faithful(ladder.whole) ?? faithful(ladder.viewport) ?? EXACT_GEOMETRY).toleranceDegrees;
}

/**
 * Tolerance of the rung one coarser than the one this tolerance identifies, which
 * is what the API bounds the span of a viewport request from. Only asked for a
 * rung the API will not serve whole, so a coarser one always exists.
 */
function escalatedFromToleranceDegrees(ladder: GeometryLadder, toleranceDegrees: number): number {
  const rungs = [...ladder.whole, ...ladder.viewport, EXACT_GEOMETRY];
  const escalated = rungs.findIndex(rung => rung.toleranceDegrees === toleranceDegrees);
  return rungs[escalated - 1].toleranceDegrees;
}

function wrapLongitude(longitude: number): number {
  if (longitude >= MIN_LONGITUDE && longitude <= MAX_LONGITUDE) {
    return longitude;
  }
  return (
    ((((longitude - MIN_LONGITUDE) % LONGITUDE_SPAN) + LONGITUDE_SPAN) % LONGITUDE_SPAN) +
    MIN_LONGITUDE
  );
}

/**
 * A camera box brought into the coordinate range the server compares against.
 * getBounds() keeps counting past the antimeridian (-190, 190) while stored
 * coordinates stay within [-180, 180], so an unwrapped bound matches nothing and
 * the geometry silently stops appearing.
 *
 * A box straddling the antimeridian cannot be one ascending range, so it keeps the
 * side `insideLongitude` (the camera) is on; the sliver past 180 draws at the
 * whole-table resolution underneath until the camera crosses over.
 */
function normalizeBbox([minLng, minLat, maxLng, maxLat]: Bbox, insideLongitude: number): Bbox {
  const south = Math.max(minLat, MIN_LATITUDE);
  const north = Math.min(maxLat, MAX_LATITUDE);
  if (maxLng - minLng >= LONGITUDE_SPAN) {
    return [MIN_LONGITUDE, south, MAX_LONGITUDE, north];
  }
  const west = wrapLongitude(minLng);
  const east = wrapLongitude(maxLng);
  if (west <= east) {
    return [west, south, east, north];
  }
  const inside = wrapLongitude(insideLongitude);
  return inside >= west ? [west, south, MAX_LONGITUDE, north] : [MIN_LONGITUDE, south, east, north];
}

/**
 * What the map canvas covers, or null until it has been measured. The dimensions
 * come from the size deck.gl measured for its own canvas, which is what makes this
 * the map's bounds rather than the window's -- the map is inset by the sidebar, so
 * a window-sized viewport asks for materially more than is on screen. Without them
 * WebMercatorViewport would assume a 1x1 canvas, so callers get null instead.
 *
 * Null rather than a box from a non-finite camera, checked before a viewport is
 * built from it: deck.gl reads a NaN longitude as 0, answering with a box at null
 * island, and throws outright on a NaN zoom ("Pixel project matrix not
 * invertible"), which would take the layer's effect with it.
 */
export function cameraBbox(camera: Camera): Bbox | null {
  const { width, height } = camera;
  if (width == null || height == null || width <= 0 || height <= 0) {
    return null;
  }
  const { longitude, latitude, zoom } = camera;
  if (![longitude, latitude, zoom].every(Number.isFinite)) {
    return null;
  }
  const box = new WebMercatorViewport({ ...camera, width, height }).getBounds();
  if (!box.every(Number.isFinite) || box[0] >= box[2] || box[1] >= box[3]) {
    return null;
  }
  return normalizeBbox(box, camera.longitude);
}

// Derived rather than read from viewStateAtom directly, and a tolerance rather
// than a rung object: a subscriber then re-renders when the resolution changes,
// not on every camera frame the view state atom emits while the user drags.
// Keyed by ladder identity, so the module-level ladders each hold one entry.
const liveFaithfulToleranceAtom = atomFamily((ladder: GeometryLadder) =>
  atom<number>(get => faithfulTolerance(ladder, get(viewStateAtom)))
);

function padBbox([minLng, minLat, maxLng, maxLat]: Bbox): Bbox {
  const lngPadding = (maxLng - minLng) * VIEWPORT_BBOX_PADDING;
  const latPadding = (maxLat - minLat) * VIEWPORT_BBOX_PADDING;
  return normalizeBbox(
    [minLng - lngPadding, minLat - latPadding, maxLng + lngPadding, maxLat + latPadding],
    // The box being padded is already one ascending range, so its own midpoint is
    // the side to keep if the padding pushes it over the antimeridian.
    (minLng + maxLng) / 2
  );
}

/**
 * Degrees the narrowed box below is placed on. Two multiples of a power of two
 * subtract exactly, so a box on this grid spans exactly what it was built to;
 * rounding the edges independently instead measures an ulp over the bound for a third
 * of camera positions, which the API's strict `>` refuses outright. 7 mm, finer than
 * the coordinates the API emits.
 */
const BBOX_GRID_DEGREES = 2 ** -24;

/** A degree value moved onto the grid, `round` deciding which way. */
function onGrid(degrees: number, round: (value: number) => number): number {
  return round(degrees / BBOX_GRID_DEGREES) * BBOX_GRID_DEGREES;
}

/**
 * The box moved outwards onto the grid, so it still covers everything it did. The
 * coordinate limits are grid multiples themselves, so clamping to them keeps the
 * box on the grid.
 */
function bboxOnGrid([minLng, minLat, maxLng, maxLat]: Bbox): Bbox {
  return [
    Math.max(onGrid(minLng, Math.floor), MIN_LONGITUDE),
    Math.max(onGrid(minLat, Math.floor), MIN_LATITUDE),
    Math.min(onGrid(maxLng, Math.ceil), MAX_LONGITUDE),
    Math.min(onGrid(maxLat, Math.ceil), MAX_LATITUDE),
  ];
}

/**
 * Widest span, in degrees, the API will serve this resolution over a box like this
 * one -- the bound its own `maxViewportSpanDegrees` applies, taken at the latitude
 * of the box nearest the equator as the API takes it, and floored onto the grid.
 *
 * Read off the box moved outwards onto the grid rather than the box itself, so the
 * latitude is never further from the equator -- where the bound is wider -- than the
 * one the API reads off whatever `clampToSpan` builds from it.
 */
function servedSpanDegrees(bbox: Bbox, escalatedFromToleranceDegrees: number): number {
  const [, south, , north] = bboxOnGrid(bbox);
  const latitude = south <= 0 && north >= 0 ? 0 : Math.min(Math.abs(south), Math.abs(north));
  const maxSpan = maxViewportSpanDegrees(escalatedFromToleranceDegrees, latitude);
  return Math.floor(maxSpan / BBOX_GRID_DEGREES) * BBOX_GRID_DEGREES;
}

/**
 * The box narrowed to `span` at most, centred on the camera, or returned untouched
 * where it already fits.
 *
 * The resolution comes from the worst latitude on screen while the API bounds the
 * span from the box latitude nearest the equator, so a view reaching north escalates
 * a rung earlier than the bound expects and can ask over a box wider than it admits
 * -- refused on a 4K canvas at low zoom, which would cost the layer its geometry
 * across the whole canvas. Ground outside the clamp keeps the backdrop.
 */
function clampToSpan(bbox: Bbox, span: number, camera: Camera): Bbox {
  if (Math.max(bbox[2] - bbox[0], bbox[3] - bbox[1]) <= span) {
    return bbox;
  }
  const [west, south, east, north] = bboxOnGrid(bbox);
  const half = span / 2;
  const narrowed = (low: number, high: number, towards: number): [number, number] => {
    if (high - low <= span) {
      return [low, high];
    }
    const centre = Math.min(Math.max(onGrid(towards, Math.round), low + half), high - half);
    return [centre - half, centre + half];
  };
  const [clampedWest, clampedEast] = narrowed(west, east, wrapLongitude(camera.longitude));
  const [clampedSouth, clampedNorth] = narrowed(south, north, camera.latitude);
  return [clampedWest, clampedSouth, clampedEast, clampedNorth];
}

function containsBbox(outer: Bbox, inner: Bbox): boolean {
  return (
    outer[0] <= inner[0] && outer[1] <= inner[1] && outer[2] >= inner[2] && outer[3] >= inner[3]
  );
}

/** A resolution to fetch, and the box to fetch it over. */
export type GeometryDetailRequest = { variant: GeometryVariant; bbox: Bbox };

export type GeometryDetail<W extends GeometryVariant = GeometryVariant> = {
  /**
   * Whole-table resolution to draw, and null until the camera first settles --
   * callers keep rendering whatever they already have until then. Where no
   * whole-table resolution is faithful this is the finest one, which stays as the
   * backdrop outside the box `detail` covers.
   */
  base: W | null;
  /**
   * Finer resolution to fetch, and the padded viewport to fetch it over, narrowed
   * to the span the API serves that resolution over. Null while `base` is faithful
   * on its own, or until the canvas has been measured. Identity is stable while the
   * settled viewport stays inside the box already fetched, so a small pan does not
   * supersede it.
   */
  detail: GeometryDetailRequest | null;
};

const UNSETTLED = { base: null, detail: null };

/**
 * What resolution of a table to draw, derived from the tolerance of each of its
 * generalized columns against the camera's zoom and the latitude on screen that
 * shows a generalization most.
 *
 * A zoom out takes effect immediately (whatever is on screen is finer than the
 * new rung needs, so it stays faithful while the cheaper geometry loads), while
 * a zoom in waits for the camera to settle, so flying past a threshold does not
 * fetch on the way through.
 */
export function useGeometryDetail<W extends GeometryVariant>(
  ladder: GeometryLadder<W>
): GeometryDetail<W> {
  const liveTolerance = useAtomValue(liveFaithfulToleranceAtom(ladder));
  const settledViewState = useAtomValue(settledViewStateAtom);
  const [detail, setDetail] = React.useState<GeometryDetail<W>>(UNSETTLED);

  // Derived while rendering rather than from an effect, so the camera that
  // settled and the resolution drawn for it reach the layers in the same pass.
  // The previous value is still state because the decision reads it: an
  // unchanged rung keeps its object, and a viewport already covered by the box
  // last requested is not requested again.
  const next = nextGeometryDetail(ladder, liveTolerance, settledViewState, detail);
  if (next !== detail) {
    setDetail(next);
  }

  return next;
}

/**
 * The resolution to draw for a camera, given what is already drawn. Returns
 * `previous` unchanged wherever the camera asks for nothing new, which is what
 * keeps a settled pan inside the box already fetched from refetching.
 */
function nextGeometryDetail<W extends GeometryVariant>(
  ladder: GeometryLadder<W>,
  liveTolerance: number,
  settledViewState: Camera | null,
  previous: GeometryDetail<W>
): GeometryDetail<W> {
  if (settledViewState == null) {
    return UNSETTLED;
  }

  // The coarser of the live and settled cameras, i.e. the larger tolerance.
  const tolerance = Math.max(liveTolerance, faithfulTolerance(ladder, settledViewState));

  const wholeRung = ladder.whole.find(rung => rung.toleranceDegrees === tolerance);
  const base = (wholeRung ?? ladder.whole[ladder.whole.length - 1]).variant;
  const inView = wholeRung === undefined ? cameraBbox(settledViewState) : null;
  if (inView == null) {
    return previous.base === base && previous.detail == null ? previous : { base, detail: null };
  }

  const variant = (
    ladder.viewport.find(rung => rung.toleranceDegrees === tolerance) ?? EXACT_GEOMETRY
  ).variant;
  const padded = padBbox(inView);
  // One span for both boxes below, taken from the box actually requested. What is
  // on screen is narrowed by it too: where the clamp bites, the request already
  // covers everything the API will serve at this resolution, and a probe against
  // the unnarrowed view would never be covered, so a settled pan that changed
  // nothing would refetch.
  const span = servedSpanDegrees(padded, escalatedFromToleranceDegrees(ladder, tolerance));
  return previous.base === base &&
    previous.detail?.variant === variant &&
    containsBbox(previous.detail.bbox, clampToSpan(inView, span, settledViewState))
    ? previous
    : { base, detail: { variant, bbox: clampToSpan(padded, span, settledViewState) } };
}
